import React, { useState, useEffect } from 'react';
import { X, ExternalLink, CheckCircle } from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { templatesAPI } from '../../services/api';
import { resolveVariables, formatWhatsAppUrl } from './templateUtils';

const SOURCE_LABEL = {
  whatsappNumber: 'using WhatsApp number',
  phone: 'using mobile number',
  primaryContactPhone: 'using primary contact',
};

const WhatsAppTemplatePanel = ({ isOpen, onClose, module, context, recipient }) => {
  const [templates, setTemplates] = useState([]);
  const [body, setBody] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [opened, setOpened] = useState(false);

  const organizationId = localStorage.getItem('organizationId');
  const charCount = body.length;

  useEffect(() => {
    if (!isOpen) return;
    fetchTemplates();
    setOpened(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, module]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await templatesAPI.getTemplates(organizationId, 'WHATSAPP', module);
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Failed to load WhatsApp templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (e) => {
    const id = e.target.value;
    if (!id) {
      setBody('');
      return;
    }
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    const resolved = resolveVariables(tpl, context || {});
    setBody(resolved.body || '');
  };

  const handleOpenWhatsApp = () => {
    if (!body.trim()) { toast.error('Message body is required'); return; }
    if (body.length > 1000) { toast.error('Message exceeds 1000 character limit'); return; }
    const url = formatWhatsAppUrl(recipient.phone, body);
    window.open(url, '_blank');
    setOpened(true);
  };

  const handleClose = () => {
    setBody('');
    setOpened(false);
    onClose();
  };

  if (!isOpen) return null;

  const sourceLabel = SOURCE_LABEL[recipient?.phoneSource] || 'using mobile number';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-white w-full max-w-lg h-full flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: '#25D366' }}>
              WhatsApp
            </p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <IoLogoWhatsapp size={18} color="#25D366" />
              {recipient?.name ? `Send WhatsApp to ${recipient.name}` : 'Send WhatsApp'}
            </h2>
            {recipient?.phone && (
              <div className="mt-1">
                <p className="text-sm text-slate-700 font-medium">
                  +91 {recipient.phone.replace(/(\d{5})(\d{5})/, '$1 $2')}
                </p>
                <p className="text-xs text-slate-400">[{sourceLabel}]</p>
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Template selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Select Template
            </label>
            <select
              onChange={handleTemplateSelect}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
              defaultValue=""
            >
              <option value="">Write custom message</option>
              {loadingTemplates ? (
                <option disabled>Loading templates…</option>
              ) : (
                templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Message body */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={1000}
              placeholder="Write your WhatsApp message here…"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
            />
            <p className={`text-right text-xs mt-1 ${charCount >= 900 ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
              {charCount}/1000
            </p>
          </div>

          {/* After open note */}
          {opened && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
              <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-green-800 font-medium">WhatsApp opened in a new tab.</p>
                <p className="text-xs text-green-600 mt-0.5">Click "Mark as Sent" once you've sent the message.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex flex-col gap-2 flex-shrink-0">
          {opened ? (
            <>
              <button
                onClick={handleClose}
                className="w-full px-4 py-2.5 text-sm font-semibold rounded-xl text-white transition-colors"
                style={{ backgroundColor: '#25D366' }}
              >
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle size={14} /> Mark as Sent
                </span>
              </button>
              <button
                onClick={handleOpenWhatsApp}
                className="w-full px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50"
              >
                Open WhatsApp Again
              </button>
            </>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleOpenWhatsApp}
                className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                style={{ backgroundColor: '#25D366' }}
              >
                <ExternalLink size={14} />
                Open WhatsApp
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplatePanel;
